import { arrayToIdKeyedObject } from '@sequencehq/utils'
import {
  Duration,
  durationsToDateAdapter,
  isAfter,
  isBefore,
  sub
} from '@sequencehq/utils/dates'
import { pricesHaveSameTerms } from 'Cube/communication/external/billingSchedule.api.v1/ports/entitySaving/usePriceDeduplication'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import {
  Discount,
  Minimum,
  Phase,
  PhasePriceMetadata,
  Price,
  RecursivePartial
} from 'Cube/domain/cube.domain.types'
import { DuplicatedPhaseData, duplicatePhase } from 'Cube/utils/duplicatePhase'
import { CubeShortcut, cubeUpdateShortcuts } from 'Cube/utils/updateShortcuts'
import { useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { match } from 'ts-pattern'

export const applyPricesFromPhase =
  (ctx: {
    sourcePhase: {
      prices: Array<Price>
    }
  }) =>
  (targetPhase: {
    id: string
    prices: Array<Price>
    discounts: Array<Pick<Discount, 'id' | 'priceIds'>>
    minimums: Array<{
      id: string
      scope: Pick<Minimum['scope'], 'priceIds'>
    }>
    phasePriceMetadata: Array<PhasePriceMetadata>
  }): {
    updatedMinimums: Record<string, RecursivePartial<Minimum>>
    updatedDiscounts: Record<string, RecursivePartial<Discount>>
    updatedPhase: Record<string, RecursivePartial<Phase>>
  } => {
    const sharedPriceIds = targetPhase.prices.reduce<Record<string, string>>(
      (acc, targetPrice) => {
        const sourcePrice = ctx.sourcePhase.prices.find(price =>
          pricesHaveSameTerms(price, targetPrice)
        )

        return sourcePrice ? { ...acc, [targetPrice.id]: sourcePrice.id } : acc
      },
      {}
    )

    const hasSharedPrices = Object.keys(sharedPriceIds).length > 0

    if (!hasSharedPrices) {
      return { updatedPhase: {}, updatedDiscounts: {}, updatedMinimums: {} }
    }

    const toSharedPriceId = (priceId: string) =>
      sharedPriceIds[priceId] ?? priceId

    return {
      updatedPhase: {
        [targetPhase.id]: {
          priceIds: targetPhase.prices.map(price => toSharedPriceId(price.id)),
          phasePriceMetadata: targetPhase.phasePriceMetadata.map(metadata => ({
            ...metadata,
            priceId: toSharedPriceId(metadata.priceId)
          }))
        }
      },
      updatedDiscounts: targetPhase.discounts.reduce<
        Record<string, RecursivePartial<Discount>>
      >(
        (acc, discount) => ({
          ...acc,
          [discount.id]: {
            priceIds: discount.priceIds.map(toSharedPriceId)
          }
        }),
        {}
      ),
      updatedMinimums: targetPhase.minimums.reduce<
        Record<string, RecursivePartial<Minimum>>
      >(
        (acc, minimum) => ({
          ...acc,
          [minimum.id]: {
            scope: {
              priceIds: minimum.scope.priceIds.map(toSharedPriceId)
            }
          }
        }),
        {}
      )
    }
  }

type PhaseWithPrices = Parameters<ReturnType<typeof applyPricesFromPhase>>[0]

type FollowingPhaseUpdates = {
  minimums: Record<string, RecursivePartial<Minimum>>
  discounts: Record<string, RecursivePartial<Discount>>
  phases: Record<string, RecursivePartial<Phase>>
}

export const sharePricesWithFollowingPhases = (ctx: {
  orderedPhases: Array<PhaseWithPrices>
  previousPhaseId?: string
  sourcePhase: Parameters<typeof applyPricesFromPhase>[0]['sourcePhase']
}): FollowingPhaseUpdates => {
  const previousPhaseIndex = ctx.orderedPhases.findIndex(
    p => p.id === ctx.previousPhaseId
  )

  const followingPhases =
    previousPhaseIndex > -1
      ? ctx.orderedPhases.slice(previousPhaseIndex + 1)
      : []

  return followingPhases.reduce<FollowingPhaseUpdates>(
    (acc, phase) => {
      const phaseUpdates = applyPricesFromPhase({
        sourcePhase: ctx.sourcePhase
      })(phase)

      return {
        phases: {
          ...acc.phases,
          ...phaseUpdates.updatedPhase
        },
        minimums: {
          ...acc.minimums,
          ...phaseUpdates.updatedMinimums
        },
        discounts: {
          ...acc.discounts,
          ...phaseUpdates.updatedDiscounts
        }
      }
    },
    { phases: {}, minimums: {}, discounts: {} }
  )
}

export const useCubeShortcuts = () => {
  const cubeCtx = useCubeContext()
  const [searchParams, setSearchParams] = useSearchParams()

  const latestPhase = useMemo(() => {
    return cubeCtx.queries.orderedPhases.at(-1)
  }, [cubeCtx.queries.orderedPhases])

  const highlightPhases = useCallback(
    (phaseIds: Array<string>) => {
      cubeCtx.mutators.updateEditor({
        highlightedEntityIds: phaseIds
      })
    },
    [cubeCtx.mutators.updateEditor]
  )

  const getCreateNewPhaseUpdate = useCallback(
    ({
      newPhaseData,
      previousPhaseId,
      newPreviousPhaseDuration
    }: {
      newPhaseData: DuplicatedPhaseData
      previousPhaseId?: string
      newPreviousPhaseDuration?: Duration
    }) => {
      const previousPhaseIndex = cubeCtx.queries.orderedPhases.findIndex(
        p => p.id === previousPhaseId
      )

      const followingPhaseUpdates = sharePricesWithFollowingPhases({
        orderedPhases: cubeCtx.queries.orderedPhases,
        previousPhaseId,
        sourcePhase: newPhaseData
      })

      return {
        phases: {
          ...(previousPhaseId && newPreviousPhaseDuration
            ? {
                [previousPhaseId]: {
                  duration: newPreviousPhaseDuration
                }
              }
            : {}),
          [newPhaseData.phase.id]: {
            ...newPhaseData.phase
          },
          ...followingPhaseUpdates.phases
        },
        common: {
          phaseIds:
            previousPhaseIndex > -1
              ? [
                  ...cubeCtx.queries.rawData.data.common.phaseIds.slice(
                    0,
                    previousPhaseIndex + 1
                  ),
                  newPhaseData.phase.id,
                  ...cubeCtx.queries.rawData.data.common.phaseIds.slice(
                    previousPhaseIndex + 1
                  )
                ]
              : [
                  ...cubeCtx.queries.rawData.data.common.phaseIds,
                  newPhaseData.phase.id
                ]
        },
        prices: arrayToIdKeyedObject(newPhaseData.prices),
        discounts: {
          ...arrayToIdKeyedObject(newPhaseData.discounts),
          ...followingPhaseUpdates.discounts
        },
        minimums: {
          ...arrayToIdKeyedObject(newPhaseData.minimums),
          ...followingPhaseUpdates.minimums
        }
      }
    },
    [cubeCtx]
  )

  /**
   * Both shortcuts append a duplicated phase, so they follow the same account
   * setting as the "Add phase" button rather than always resetting.
   */
  const newPhaseRecurrencePreference =
    cubeCtx.queries.rawData.configuration.editorDefaults.resetBillingPeriod

  const applyCubeShortcut = useCallback(
    (cubeShortcut: CubeShortcut) => {
      match(cubeShortcut)
        .with({ type: 'upgrade' }, cs => {
          /**
           * When upgrading, we will look for the latest phase before the upgrade date. We will
           * then insert the phase after that phase, whilst retaining the total duration of that phase.
           * i.e. if the phase is one year, and our upgrade date is 7 months in, the original phase will
           * be 7 months, and the new phase will be 5 months.
           *
           * If the phase being upgraded is the last phase, we will set the duration of the new phase to be
           * a standard 1 year.
           *
           */
          const latestPhaseBeforeUpgradeDate =
            cubeCtx.queries.orderedPhases.find(p => {
              if (!p.dates.absolute.start) {
                return
              }

              return (
                isBefore(p.dates.absolute.start, cs.startDate) &&
                (!p.dates.absolute.end ||
                  isAfter(p.dates.absolute.end, cs.startDate))
              )
            })

          if (!latestPhaseBeforeUpgradeDate) {
            return
          }

          const isLatestPhaseLastPhase =
            latestPhaseBeforeUpgradeDate.id === latestPhase?.id

          const newDurationForLastestPhase = durationsToDateAdapter.toDuration(
            latestPhaseBeforeUpgradeDate.dates.absolute.start
          )(sub(cs.startDate, { days: 1 }))

          const durationForUpgradePhase = isLatestPhaseLastPhase
            ? {
                years: 1,
                days: -1
              }
            : durationsToDateAdapter.toDuration(cs.startDate)(
                latestPhaseBeforeUpgradeDate.dates.absolute.end
              )

          if (!newDurationForLastestPhase || !durationForUpgradePhase) {
            return
          }

          const duplicatePhaseData = duplicatePhase({
            phase: latestPhaseBeforeUpgradeDate,
            recurrencePreference: newPhaseRecurrencePreference
          })

          const update = getCreateNewPhaseUpdate({
            newPhaseData: {
              ...duplicatePhaseData,
              phase: {
                ...duplicatePhaseData.phase,
                name: 'Upgrade',
                duration: durationForUpgradePhase
              }
            },
            previousPhaseId: latestPhaseBeforeUpgradeDate.id,
            newPreviousPhaseDuration: newDurationForLastestPhase
          })

          cubeCtx.mutators.updateData(update)
          highlightPhases([duplicatePhaseData.phase.id])
        })
        .with({ type: 'renew' }, cs => {
          /**
           * When renewing, we will take the phase to renew, duplicate it, and
           * append it to the end of the schedule.
           */
          const phaseToRenew = cubeCtx.queries.orderedPhases.at(cs.phaseIndex)

          if (!phaseToRenew) {
            return
          }

          const duplicatePhaseData = duplicatePhase({
            phase: phaseToRenew,
            recurrencePreference: newPhaseRecurrencePreference
          })

          const update = getCreateNewPhaseUpdate({
            newPhaseData: {
              ...duplicatePhaseData,
              phase: {
                ...duplicatePhaseData.phase,
                name: 'Renewal'
              }
            }
          })
          cubeCtx.mutators.updateData(update)
          highlightPhases([duplicatePhaseData.phase.id])
        })
        .exhaustive()
    },
    [
      cubeCtx.mutators.updateData,
      getCreateNewPhaseUpdate,
      highlightPhases,
      latestPhase,
      cubeCtx.queries.orderedPhases,
      newPhaseRecurrencePreference
    ]
  )

  /**
   * Apply any cube shortcut found, once the schedule has been loaded in.
   */
  useEffect(() => {
    const cubeShortcutUuid = searchParams.get(cubeUpdateShortcuts.queryParam)

    if (
      cubeCtx.queries.rawData.data.common.id !== 'uninitialized' &&
      cubeShortcutUuid
    ) {
      /**
       * It is possible to configure Cube to apply a preset modification to
       * the phases/schedule on load via the 'cube shortcut' mechanism. This bridges
       * the gap until we have schedule versions and all kinds of BE gubbins
       */
      const cubeShortcutUuid = searchParams.get(cubeUpdateShortcuts.queryParam)
      const cubeShortcut = cubeShortcutUuid
        ? cubeUpdateShortcuts.get(cubeShortcutUuid)
        : null

      if (cubeShortcut) {
        applyCubeShortcut(cubeShortcut)
      }

      searchParams.delete(cubeUpdateShortcuts.queryParam)
      /**
       * Reset the search params, with the shortcut uuid removed.
       */
      setSearchParams(searchParams, {
        replace: true
      })
    }
  }, [cubeCtx, applyCubeShortcut, searchParams, setSearchParams])

  return {
    applyCubeShortcut
  }
}
